import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Modal,
  message,
  Skeleton,
  Empty,
  Checkbox,
  Tooltip,
} from "antd";
import {
  FaBuilding,
  FaSearch,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import ClinicFormModal from "./ClinicFormModal";
import ClinicCard from "./ClinicCard";
import PageTitle from "../../Ui/PageTitle";
import { useClinics } from "../../../hooks/useClinics";
import BulkInvoiceModal from "../../Modals/BulkInvoiceModal";


const Clinics = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [currentClinic, setCurrentClinic] = useState(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [clinicToDelete, setClinicToDelete] = useState(null);
  const [selectedClinics, setSelectedClinics] = useState([]);
  const [bulkDeleteVisible, setBulkDeleteVisible] = useState(false);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkError, setNetworkError] = useState(false);
const [bulkModalVisible, setBulkModalVisible] = useState(false);
const [clinicId,setClinicId]=useState(null);

  const { clinicsQuery, addClinic, updateClinic, deleteClinic } = useClinics();
const role= localStorage.getItem("role"); 
  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkError(false);
      if (clinicsQuery.isError) {
        clinicsQuery.refetch();
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      setNetworkError(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [clinicsQuery]);

  // Check for network errors in queries
  useEffect(() => {
    if (clinicsQuery.isError) {
      const error = clinicsQuery.error;
      if (
        !navigator.onLine ||
        error?.code === "NETWORK_ERROR" ||
        error?.message?.includes("network") ||
        error?.message?.includes("fetch")
      ) {
        setNetworkError(true);
      }
    }
  }, [clinicsQuery.isError, clinicsQuery.error]);

  const filteredClinics = useMemo(() => {
    if (!clinicsQuery.data?.clinics) return [];

    return clinicsQuery.data.clinics.filter((clinic) => {
      if (!clinic) return false;

      const name = (clinic.name || "").toLowerCase();
      const address = (clinic.detailedAddress || "").toLowerCase();
      const email = (clinic.contactEmail || "").toLowerCase();
      const phone = clinic.contactPhone || "";
      const searchLower = searchTerm.toLowerCase();

      return (
        name.includes(searchLower) ||
        address.includes(searchLower) ||
        email.includes(searchLower) ||
        phone.includes(searchTerm)
      );
    });
  }, [clinicsQuery.data?.clinics, searchTerm]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      switch (e.key) {
        case "n":
        case "N":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            showModal();
          }
          break;
        case "/":
          e.preventDefault();
          document.querySelector('input[placeholder*="Search"]')?.focus();
          break;
        case "?":
          e.preventDefault();
          setShowKeyboardHelp(true);
          break;
        case "Escape":
          setSelectedClinics([]);
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const showModal = (clinic = null) => {
    setCurrentClinic(clinic);
    setModalVisible(true);
  };

const handleFinish = async (values) => {
    if (!navigator.onLine) {
      message.error("No internet connection. Please check your network and try again.");
      return;
    }

    try {
   const payload = {
    order: values.order,
    name: values.name,
    nameAr: values.nameAr,
    hasLocation: values.hasLocation,
    defaultReportType: values.defaultReportType ?? 0,   // ← add this
    detailedAddress: values.hasLocation ? values.detailedAddress : undefined,
    detailedAddressAr: values.hasLocation ? values.detailedAddressAr : undefined,
    contactPhone: values.hasLocation ? values.phone : undefined,
    contactEmail: values.hasLocation ? values.email : undefined,
    branchName: !values.hasLocation ? values.branchName : undefined,
};

      if (currentClinic) {
        await updateClinic.mutateAsync({ id: currentClinic.clinicId, ...payload });
        message.success("Clinic updated successfully");
      } else {
        await addClinic.mutateAsync(payload);
        message.success("Clinic added successfully");
      }
      setModalVisible(false);
      setNetworkError(false);
    } catch (error) {
      console.error("Operation failed:", error);
      if (
        !navigator.onLine ||
        error.code === "NETWORK_ERROR" ||
        error.message?.includes("network") ||
        error.message?.includes("fetch")
      ) {
        setNetworkError(true);
        message.error("Network error: Please check your internet connection and try again.");
      } else {
        message.error(error.message || "Something went wrong. Please try again.");
      }
    }
  };

  const handleDelete = (id) => {
    setClinicToDelete(id);
    setDeleteConfirmVisible(true);
  };

  const confirmDelete = async () => {
    if (!navigator.onLine) {
      message.error(
        "No internet connection. Please check your network and try again."
      );
      return;
    }

    try {
      await deleteClinic.mutateAsync(clinicToDelete);
      message.success("Clinic deleted successfully");
      setDeleteConfirmVisible(false);
      setNetworkError(false);
    } catch (error) {
      console.error("Delete failed:", error);

      if (
        !navigator.onLine ||
        error.code === "NETWORK_ERROR" ||
        error.message?.includes("network") ||
        error.message?.includes("fetch")
      ) {
        setNetworkError(true);
        message.error(
          "Network error: Please check your internet connection and try again."
        );
      } else {
        message.error(
          error.message || "Failed to delete clinic. Please try again."
        );
      }
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedClinics(filteredClinics.map((clinic) => clinic.clinicId));
    } else {
      setSelectedClinics([]);
    }
  };

  const handleSelectClinic = (clinicId, checked) => {
    if (checked) {
      setSelectedClinics((prev) => [...prev, clinicId]);
    } else {
      setSelectedClinics((prev) => prev.filter((id) => id !== clinicId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedClinics.length === 0) {
      message.warning("Please select clinics to delete");
      return;
    }
    setBulkDeleteVisible(true);
  };

  const confirmBulkDelete = async () => {
    if (!navigator.onLine) {
      message.error(
        "No internet connection. Please check your network and try again."
      );
      return;
    }

    try {
      await Promise.all(
        selectedClinics.map((id) => deleteClinic.mutateAsync(id))
      );
      message.success(`${selectedClinics.length} clinics deleted successfully`);
      setSelectedClinics([]);
      setBulkDeleteVisible(false);
      setNetworkError(false);
    } catch (error) {
      console.error("Bulk delete failed:", error);

      if (
        !navigator.onLine ||
        error.code === "NETWORK_ERROR" ||
        error.message?.includes("network") ||
        error.message?.includes("fetch")
      ) {
        setNetworkError(true);
        message.error(
          "Network error: Please check your internet connection and try again."
        );
      } else {
        message.error("Failed to delete some clinics. Please try again.");
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  return (
    <div className="animate-fade-in md:p-8">
      <div className="flex flex-col md:flex-row mb-3 items-center justify-between">
        {role==3&&
        <PageTitle
          title="Clinics Management"
          subtitle="Manage your clinics, their locations and staff"
          actionProps={{
            disabled: !isOnline || networkError || addClinic.isLoading,
            loading: addClinic.isLoading,
            "data-tip": "add-clinic",
          }}
        />}
        {role==3&&
        <Button
          type=""
          size="large"
          className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
          onClick={() => showModal()}
        >
          + Add New Clinic
        </Button>}
        
      </div>

      {/* Search Bar */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search clinics"
              prefix={<FaSearch className="text-gray-400" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              onClear={clearSearch}
              size="large"
              data-tip="search"
            />
          </div>
        </div>

        {/* Bulk Actions */}
        {filteredClinics?.length > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={selectedClinics.length === filteredClinics.length}
                indeterminate={
                  selectedClinics.length > 0 &&
                  selectedClinics.length < filteredClinics.length
                }
                onChange={(e) => handleSelectAll(e.target.checked)}
              >
                Select All ({filteredClinics.length})
              </Checkbox>

              {selectedClinics.length > 0 && (
                <Button
                  danger
                  icon={<FaTrash />}
                  onClick={handleBulkDelete}
                  loading={deleteClinic.isLoading}
                  disabled={!isOnline || networkError}
                >
                  Delete Selected ({selectedClinics.length})
                </Button>
              )}
            </div>

            <div className="text-sm text-gray-500">
              Showing {filteredClinics.length} of{" "}
              {clinicsQuery.data?.clinics?.length || 0} clinics
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {clinicsQuery.isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border p-4">
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          ))}
        </div>
      ) : clinicsQuery.isError ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <span className="text-red-500 block mb-2">
                  Failed to load clinics
                </span>
                <span className="text-gray-500 text-sm">
                  {clinicsQuery.error?.message ||
                    "Please check your connection and try again"}
                </span>
              </div>
            }
          >
            <Button
              type="primary"
              onClick={() => clinicsQuery.refetch()}
              loading={clinicsQuery.isFetching}
            >
              Try Again
            </Button>
          </Empty>
        </div>
      ) : filteredClinics.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <FaBuilding size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium mb-2">
            {searchTerm ? "No matching clinics found" : "No clinics added yet"}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm
              ? `Try adjusting your search term "${searchTerm}"`
              : "Start by adding your first clinic to get organized"}
          </p>
          {searchTerm ? (
            <Button onClick={clearSearch}>Clear Search</Button>
          ) : (
            <Button
              type="primary"
              icon={<FaPlus />}
              onClick={() => showModal()}
              size="large"
              disabled={!isOnline || networkError}
              data-tip="add-clinic"
            >
              Add Your First Clinic
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredClinics.map((clinic) => (
            <div key={clinic.clinicId} className="relative">
              {filteredClinics.length > 1 && (
                <Checkbox
                  className="absolute top-2 left-2 z-10 bg-white rounded shadow-sm"
                  checked={selectedClinics.includes(clinic.clinicId)}
                  onChange={(e) =>
                    handleSelectClinic(clinic.clinicId, e.target.checked)
                  }
                />
              )}
              <ClinicCard
                clinic={{
                  ...clinic,
                  address: clinic.detailedAddress,
                  phone: clinic.contactPhone,
                  email: clinic.contactEmail,
                }}
                onEdit={() => showModal(clinic)}
                onDelete={() => handleDelete(clinic.clinicId)}
                loading={
                  deleteClinic.isLoading &&
                  deleteClinic.variables === clinic.clinicId
                }
                selected={selectedClinics.includes(clinic.clinicId)}
                disabled={!isOnline || networkError}
                role={role}
                setBulkModalVisible={setBulkModalVisible}
                setClinicId={setClinicId}
              />
            </div>
          ))}

          {addClinic.isLoading && (
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 bg-blue-200 rounded animate-pulse"></div>
                <span className="text-sm text-blue-600">
                  Creating clinic...
                </span>
              </div>
              <Skeleton active paragraph={{ rows: 3 }} />
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      <ClinicFormModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onFinish={handleFinish}
initialValues={
    currentClinic
        ? {
            order: currentClinic.order,
            name: currentClinic.clinicName,
            nameAr: currentClinic.clinicNameAr,
            email: currentClinic.contactEmail,
            phone: currentClinic.contactPhone,
            detailedAddress: currentClinic.detailedAddress,
            detailedAddressAr: currentClinic.detailedAddressAr,
            branchName: currentClinic.branchName,
            hasLocation: currentClinic.hasLocation ?? true,
            defaultReportType: currentClinic.defaultReportType ?? 0,  // ← add this
          }
        : null
}
        
        
        loading={addClinic.isLoading || updateClinic.isLoading}
        existingClinics={clinicsQuery.data?.clinics || []}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Delete"
        open={deleteConfirmVisible}
        onOk={confirmDelete}
        onCancel={() => setDeleteConfirmVisible(false)}
        okText="Delete"
        okButtonProps={{
          danger: true,
          loading: deleteClinic.isLoading,
        }}
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to delete this clinic? This action cannot be
          undone and will remove all associated data.
        </p>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        title={`Delete ${selectedClinics.length} Clinics`}
        open={bulkDeleteVisible}
        onOk={confirmBulkDelete}
        onCancel={() => setBulkDeleteVisible(false)}
        okText={`Delete ${selectedClinics.length} Clinics`}
        okButtonProps={{
          danger: true,
          loading: deleteClinic.isLoading,
        }}
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{selectedClinics.length}</strong> selected clinics? This
          action cannot be undone and will remove all associated data.
        </p>
      </Modal>

    
           <BulkInvoiceModal
        visible={clinicId !== null }
        onCancel={() => setClinicId(null)}
        onSuccess={() => setClinicId(null)}
        // isMobile={isMobile}
        clinicId={clinicId}
      />
    </div>
  );
};

export default Clinics;